import { useState } from 'react';
import { siteInfo } from '@/mocks/novels';

export default function ContactInfo() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const rawFormData = new FormData(form);

    const honeypotValue = (rawFormData.get('company_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    const name = (rawFormData.get('name') as string || '').trim();
    const email = (rawFormData.get('email') as string || '').trim();
    const message = (rawFormData.get('message') as string || '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setErrorMsg('请填写所有必填字段');
      return;
    }

    if (message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setStatus('submitting');

    try {
      const payload = new URLSearchParams();
      payload.append('name', name);
      payload.append('email', email);
      payload.append('message', message);
      payload.append('formId', 'contact-form-about');

      const response = await fetch('https://readdy.ai/api/form/d9qm8ffnahb7382ovc4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setStatus('error');
        setErrorMsg(msg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查连接后重试');
    }
  };

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-sm text-foreground-500">有任何问题或建议？我们很乐意听到你的声音</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
          <div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                  <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                    {siteInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-lg text-accent-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服热线</h3>
                  <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                    {siteInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-lg text-secondary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-lg overflow-hidden h-52">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3416.8552875876!2d121.49978631514347!3d31.233471881458!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35b27058de23f2e3%3A0x8e3ab2e16d3ff59f!2sLujiazui%2C%20Pudong%2C%20Shanghai!5e0!3m2!1sen!2scn!4v1690000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="公司位置"
              ></iframe>
            </div>
          </div>

          <div>
            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="bg-background-100 rounded-lg p-5 md:p-6 space-y-4"
            >
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200 text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
              </div>
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 font-semibold rounded-lg text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {status === 'submitting' ? '发送中...' : '发送留言'}
              </button>

              {status === 'success' && (
                <p className="text-sm text-accent-600 text-center font-medium">留言发送成功！我们会尽快回复您～</p>
              )}
              {status === 'error' && (
                <p className="text-sm text-accent-500 text-center">{errorMsg}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}